import { Injectable } from '@angular/core';
import { identity, pickBy } from 'lodash-es';
import { from, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, retryWhen, timeout, toArray } from 'rxjs/operators';
import { Clinic, ClinicService } from 'src/app/core/api/clinic.service';
import { CreateQuestionnaireRequestInput, QuestionnaireType } from '../../../../API';
import { Patient } from '../../../core/api/patient.service';
import {
  QuestionnaireRequest,
  QuestionnaireRequestService
} from '../../../core/api/questionnaire-request.service';
import {
  describeRequestError,
  retryTransientRequestErrors
} from '../import-wizard-request-retry';

// One questionnaire that could not be sent, kept so the wizard can tell the user exactly which
// patients did not get their questionnaire and why — instead of reporting blanket success.
export interface QuestionnaireSendFailure {
  name: string;
  reason: string;
}

// Result of the send phase: the requests that were sent AND the ones that failed.
// Nothing is discarded silently.
export interface SendQuestionnairesResult {
  sent: QuestionnaireRequest[];
  failures: QuestionnaireSendFailure[];
}

// Outcome of a single send attempt inside the bounded-concurrency stream.
interface QuestionnaireSendOutcome {
  questionnaireRequest: QuestionnaireRequest | null;
  failure: QuestionnaireSendFailure | null;
}

// A request that is already fully built and just needs dispatching. `displayName` is what the
// failure list shows, so the caller decides whether that is a patient name, a questionnaire name,
// or both.
export interface PreparedQuestionnaireSend {
  input: CreateQuestionnaireRequestInput;
  displayName: string;
  /** Set to also create an assessment linked to the request, as the import wizard does. */
  patientIdForAssessment?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ImportWizardSendQuestionnairesService {
  // Same bounded-concurrency pattern as the create-patient phase: 12 in-flight at a time
  // keeps AppSync happy and the browser responsive even for 19K-patient sends.
  private readonly maxConcurrentRequests = 12;
  // Generous per-request hang-guard (see the create phase for rationale). Set ABOVE realistic
  // latency; the old 10s value silently timed out valid sends.
  private readonly perRequestTimeoutMs = 60000;
  // Retry budget for provably-transient rejections (see retryTransientRequestErrors) before
  // giving up and recording the send as a failure — a failure is reported, never dropped.
  private readonly maxRetryAttempts = 3;
  private readonly retryBackoffBaseMs = 500;

  constructor(
    private questionnaireRequestService: QuestionnaireRequestService,
    private clinicService: ClinicService
  ) {}

  public questionnaireInputs(
    patients: Patient[],
    questionnaireTypes: QuestionnaireType[],
    sendViaEmails: boolean[],
    sendViaPhones: boolean[],
    clinicCountry: string = ''
  ): { questionnaireReqInput: CreateQuestionnaireRequestInput; patientId: string }[] {
    const questionnaireCreations = patients
      .map((patient, index) => {
        const input: CreateQuestionnaireRequestInput & {
          id: string;
        } = this.questionnaireRequestService.createFormGroup({
          questionnaireType: questionnaireTypes[index],
          patient: {
            ...patient,
            nonModifiableData: patient.nonModifiableData
              ? JSON.stringify(patient.nonModifiableData)
              : '{}',
            ...{ clinic: { country: clinicCountry } as Clinic }
          }
        }).value;
        input.email = sendViaEmails[index] && !!input.email ? input.email : undefined;
        input.mobile = sendViaPhones[index] && !!input.mobile ? input.mobile : undefined;
        return { questionnaireReqInput: input, patientId: patient.id };
      })
      .filter(
        questionnaireReqAndPatientId =>
          !!questionnaireReqAndPatientId.questionnaireReqInput.email ||
          !!questionnaireReqAndPatientId.questionnaireReqInput.mobile
      );
    return questionnaireCreations;
  }

  public sendQuestionnaires(
    patients: Patient[],
    questionnaireTypes: QuestionnaireType[],
    sendViaEmails: boolean[],
    sendViaPhones: boolean[],
    createAssessmentAndSendQuestionnaire: boolean = true
  ): Observable<SendQuestionnairesResult> {
    const patientDisplayNameById = this.buildPatientDisplayNameById(patients);
    return this.clinicService.getLoggedInClinicCountry().pipe(
      mergeMap(country =>
        this.sendPreparedRequests(
          this.questionnaireInputs(
            patients,
            questionnaireTypes,
            sendViaEmails,
            sendViaPhones,
            country
          ).map(questionnaireReqAndPatientId => ({
            input: questionnaireReqAndPatientId.questionnaireReqInput,
            displayName: this.lookUpPatientDisplayName(
              patientDisplayNameById,
              questionnaireReqAndPatientId.patientId
            ),
            patientIdForAssessment: createAssessmentAndSendQuestionnaire
              ? questionnaireReqAndPatientId.patientId
              : undefined
          }))
        )
      )
    );
  }

  /**
   * Dispatches already-built requests with bounded concurrency, a per-request hang guard and a
   * retry budget for transient rejections. Every send that still fails is reported in `failures`
   * rather than dropped, so no caller can claim blanket success.
   */
  public sendPreparedRequests(
    preparedSends: PreparedQuestionnaireSend[]
  ): Observable<SendQuestionnairesResult> {
    if (!preparedSends || preparedSends.length === 0) {
      return of({ sent: [], failures: [] } as SendQuestionnairesResult);
    }

    return from(preparedSends).pipe(
      mergeMap(
        preparedSend =>
          this.dispatch(preparedSend).pipe(
            timeout(this.perRequestTimeoutMs),
            // Retry ONLY provably-transient rejections (throttles) with backoff. Timeouts
            // and unknown errors are never replayed: re-issuing a send that may have
            // committed would text/email the same patient twice.
            retryWhen(retryTransientRequestErrors(this.maxRetryAttempts, this.retryBackoffBaseMs)),
            map(questionnaireRequest =>
              this.toSendOutcome(questionnaireRequest, preparedSend.displayName)
            ),
            // A send that still fails after retries is RECORDED, never dropped — so the
            // caller can tell the user exactly who did not get a questionnaire and why.
            catchError(sendError =>
              of({
                questionnaireRequest: null,
                failure: {
                  name: preparedSend.displayName,
                  reason: describeRequestError(sendError)
                }
              } as QuestionnaireSendOutcome)
            )
          ),
        this.maxConcurrentRequests
      ),
      toArray(),
      map((outcomes: QuestionnaireSendOutcome[]) => this.collectSendOutcomes(outcomes))
    );
  }

  private dispatch(preparedSend: PreparedQuestionnaireSend): Observable<QuestionnaireRequest> {
    if (preparedSend.patientIdForAssessment) {
      return this.questionnaireRequestService.createAssessmentAndSendQuestionnaireRequest(
        pickBy(preparedSend.input, identity) as CreateQuestionnaireRequestInput,
        preparedSend.patientIdForAssessment
      );
    }
    return this.questionnaireRequestService.createQuestionnaireRequest(preparedSend.input);
  }

  private toSendOutcome(
    questionnaireRequest: QuestionnaireRequest,
    displayName: string
  ): QuestionnaireSendOutcome {
    // A "successful" response with no request body still means nothing was sent — record it as
    // a failure rather than letting it fall through both buckets.
    if (!questionnaireRequest) {
      return {
        questionnaireRequest: null,
        failure: {
          name: displayName,
          reason: 'Server returned no response'
        }
      };
    }
    return { questionnaireRequest: questionnaireRequest, failure: null };
  }

  private lookUpPatientDisplayName(
    patientDisplayNameById: { [patientId: string]: string },
    patientId: string
  ): string {
    return patientDisplayNameById[patientId]
      ? patientDisplayNameById[patientId]
      : 'Unnamed patient';
  }

  private buildPatientDisplayNameById(patients: Patient[]): { [patientId: string]: string } {
    const patientDisplayNameById: { [patientId: string]: string } = {};
    patients.forEach(patient => {
      if (!patient || !patient.id) {
        return;
      }
      const firstName = patient.firstName ? patient.firstName : '';
      const lastName = patient.lastName ? patient.lastName : '';
      const fullName = (firstName + ' ' + lastName).trim();
      patientDisplayNameById[patient.id] = fullName ? fullName : 'Unnamed patient';
    });
    return patientDisplayNameById;
  }

  private collectSendOutcomes(outcomes: QuestionnaireSendOutcome[]): SendQuestionnairesResult {
    const sent: QuestionnaireRequest[] = [];
    const failures: QuestionnaireSendFailure[] = [];
    outcomes.forEach(outcome => {
      if (outcome.questionnaireRequest) {
        sent.push(outcome.questionnaireRequest);
      } else if (outcome.failure) {
        failures.push(outcome.failure);
      }
    });
    return { sent: sent, failures: failures };
  }
}
